import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ClipboardCheck, AlertTriangle, CheckCircle, Clock, FileText,
  Pill, Users, Activity, Heart, TrendingUp, TrendingDown, DollarSign,
  Calendar, Shield, Brain, Loader2, ChevronRight, AlertCircle,
  FileSignature, Pen, RefreshCw, User, Scale, Stethoscope,
  Home, Building, Wind, ArrowRight, Save, Send, Check, X,
  ChevronDown, ChevronUp, Sparkles
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Compliance status colors
const STATUS_COLORS = {
  green: { bg: "bg-green-100", border: "border-green-500", text: "text-green-700", badge: "bg-green-500" },
  yellow: { bg: "bg-yellow-100", border: "border-yellow-500", text: "text-yellow-700", badge: "bg-yellow-500" },
  red: { bg: "bg-red-100", border: "border-red-500", text: "text-red-700", badge: "bg-red-500" }
};

// Level of Care options
const LOC_OPTIONS = [
  { value: "RHC", label: "Routine Home Care", description: "Standard hospice care at home" },
  { value: "GIP", label: "General Inpatient", description: "Acute symptom management in facility" },
  { value: "CHC", label: "Continuous Home Care", description: "Crisis care at home (8+ hours)" },
  { value: "Respite", label: "Respite Care", description: "Caregiver relief (up to 5 days)" }
];

export default function IDGSummaryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patientId, setPatientId] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [meetingId, setMeetingId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    compliance: true,
    clinical: true,
    medications: true,
    psychosocial: true,
    plan: true,
    financial: true
  });
  
  // Dialog states
  const [showMedUpdateDialog, setShowMedUpdateDialog] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [showNarrativeDialog, setShowNarrativeDialog] = useState(false);
  const [showLOCDialog, setShowLOCDialog] = useState(false);
  const [showFrequencyDialog, setShowFrequencyDialog] = useState(false);
  
  // Form states
  const [selectedMed, setSelectedMed] = useState(null);
  const [newDose, setNewDose] = useState("");
  const [doseReason, setDoseReason] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [narrative, setNarrative] = useState("");
  const [newLOC, setNewLOC] = useState("");
  const [locRationale, setLocRationale] = useState("");
  const [visitFrequency, setVisitFrequency] = useState({ rn: 2, hha: 0, msw: 1, chaplain: 1 });
  const [frequencyNotes, setFrequencyNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("hospice_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch IDG Summary for a patient
  const fetchSummary = useCallback(async (pid) => {
    if (!pid) return;
    setLoading(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/ehr/idg-summary/patient/${pid}`, {
        headers: getAuthHeaders()
      });
      setSummaryData(response.data);
      
      // Auto-create meeting if needed
      if (!meetingId) {
        const meetingRes = await axios.post(`${API_URL}/api/ehr/idg-summary/meeting/create`, {
          patient_id: pid,
          attendees: []
        }, { headers: getAuthHeaders() });
        setMeetingId(meetingRes.data.meeting_id);
      }
    } catch (err) {
      console.error("Failed to fetch IDG summary:", err);
      // Demo data for testing
      setSummaryData(getDemoData(pid));
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  // Demo data function
  const getDemoData = (pid) => ({
    patient_id: pid || "demo-patient",
    patient_name: "John Smith",
    generated_at: new Date().toISOString(),
    lookback_days: 14,
    benefit_period: "2nd",
    days_in_program: 125,
    current_loc: "RHC",
    admission_date: "2025-09-22",
    
    compliance_health_bar: {
      hope_status: {
        status: "yellow",
        message: "HOPE update due in 3 days",
        days_until_due: 3,
        update_due: false
      },
      f2f_status: {
        valid: true,
        valid_through: "2026-03-15",
        required: true,
        message: "Valid through March 15, 2026"
      },
      documentation_grade: {
        grade: "B",
        ai_alert: "Add objective weight data to improve score."
      },
      sfv_trigger: {
        active: true,
        triggers: [{ reason: "Pain follow-up", due_date: "2026-01-27", completed: "2026-01-26" }],
        note: "48-hour Pain follow-up visit completed"
      }
    },
    
    clinical_changes: {
      symptom_impact: [
        { symptom: "Pain", previous_score: 1, current_score: 2, previous_label: "Mild", current_label: "Moderate", direction: "increased", alert_level: "high", change_date: "2026-01-24" },
        { symptom: "Dyspnea", previous_score: 0, current_score: 2, previous_label: "None", current_label: "Moderate with activity", direction: "increased", alert_level: "high", change_date: "2026-01-24" }
      ],
      functional_status: {
        mobility: "Assistance of 1",
        transfer_status: "Assistance of 1",
        adl_score: 14,
        notes: "Patient shifted from Independent to Assistance of 1 for transfers"
      },
      weight_trend: {
        current_weight: 142,
        weight_change: -8.5,
        direction: "Loss",
        period_days: 30,
        alert: true
      },
      recent_incidents: [
        { type: "Fall", date: "2026-01-20", description: "Fall from bed to floor", equipment_ordered: "Floor mat, Low bed", status: "Documented" }
      ]
    },
    
    medication_insights: {
      high_risk_meds: [
        { name: "Morphine Sulfate 15mg", dose: "Every 4 hours", reason: "Opioid with recent dose increase", recent_dose_change: true },
        { name: "Lorazepam 0.5mg", dose: "TID", reason: "Beers Criteria - Benzodiazepine", recent_dose_change: false }
      ],
      unrelated_meds: [
        { name: "Metformin 500mg", dose: "BID", reason: "Listed on Addendum (unrelated to terminal diagnosis)" }
      ],
      new_orders_since_last_idg: [
        { type: "DME", item: "Hospital Bed - Low Height", ordered_date: "2026-01-21", status: "Delivered" },
        { type: "Supply", item: "Floor Mat (Fall Prevention)", ordered_date: "2026-01-21", status: "Delivered" },
        { type: "Medication", item: "Morphine dose increase 10mg→15mg", ordered_date: "2026-01-23", status: "Active" }
      ],
      total_active_medications: 8
    },
    
    psychosocial_spiritual: {
      patient_goal: "Remain at home with family; pain control priority",
      caregiver_burden: {
        level: "High",
        notes: "Increased caregiver fatigue noted; spouse showing signs of burnout. Respite discussed.",
        respite_discussed: true
      },
      spiritual_support: {
        chaplain_visits_last_period: 2,
        preference: "Catholic",
        accepting_visits: true
      }
    },
    
    collaborative_plan: {
      loc_recommendation: {
        current: "RHC",
        recommended: "RHC",
        options: ["RHC", "GIP", "CHC", "Respite"]
      },
      visit_frequency: {
        current: { rn: 2, hha: 2, msw: 1, chaplain: 1 },
        adjustments_needed: true
      },
      team_signatures: [],
      recertification_due: true
    },
    
    financial_oversight: {
      per_patient_day: 285.50,
      threshold: 250.00,
      exceeds_threshold: true,
      percentile: "90th+",
      cost_breakdown: { medications: 1200, supplies: 450, dme: 850 },
      period_days: 14,
      alert: "Financial Alert: Spend is in the 90th percentile. Review DME/Supply necessity."
    },
    
    qapi_compliance: {
      last_idg_review: "2026-01-11",
      days_since_review: 14,
      review_due: true,
      hope_discussed_at_last_review: true,
      compliant: false
    },
    
    pre_populated_percentage: 90,
    requires_discussion: ["HOPE Update", "Symptom Changes", "Weight Alert", "Recent Incidents", "High-Risk Medications", "Financial Alert", "Recertification"]
  });

  // Handle patient search
  const handleSearch = async () => {
    if (patientSearch.trim()) {
      setPatientId(patientSearch.trim());
      await fetchSummary(patientSearch.trim());
    }
  };

  // Live medication update
  const handleMedUpdate = async () => {
    if (!selectedMed || !newDose) return;
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/ehr/idg-summary/medication/live-update`, {
        patient_id: patientId,
        medication_id: selectedMed.id || "demo-med",
        new_dose: newDose,
        reason: doseReason,
        update_mar: true,
        notify_pharmacy: true
      }, { headers: getAuthHeaders() });
      
      toast.success("Medication updated across MAR, Orders, and Pharmacy feed");
      setShowMedUpdateDialog(false);
      setSelectedMed(null);
      setNewDose("");
      setDoseReason("");
      fetchSummary(patientId);
    } catch (err) {
      toast.error("Failed to update medication");
    } finally {
      setSubmitting(false);
    }
  };

  // Update LOC
  const handleLOCUpdate = async () => {
    if (!newLOC || !meetingId) return;
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/ehr/idg-summary/meeting/${meetingId}/loc-recommendation`, {
        patient_id: patientId,
        meeting_id: meetingId,
        recommendation: newLOC,
        rationale: locRationale
      }, { headers: getAuthHeaders() });
      
      toast.success(`Level of Care updated to ${newLOC}`);
      setShowLOCDialog(false);
      fetchSummary(patientId);
    } catch (err) {
      toast.error("Failed to update Level of Care");
    } finally {
      setSubmitting(false);
    }
  };

  // Update visit frequency
  const handleFrequencyUpdate = async () => {
    if (!meetingId) return;
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/ehr/idg-summary/meeting/${meetingId}/visit-frequency`, {
        patient_id: patientId,
        meeting_id: meetingId,
        rn_visits_per_week: visitFrequency.rn,
        hha_visits_per_week: visitFrequency.hha,
        msw_visits_per_week: visitFrequency.msw,
        chaplain_visits_per_week: visitFrequency.chaplain,
        notes: frequencyNotes
      }, { headers: getAuthHeaders() });
      
      toast.success("Visit frequency updated");
      setShowFrequencyDialog(false);
      fetchSummary(patientId);
    } catch (err) {
      toast.error("Failed to update visit frequency");
    } finally {
      setSubmitting(false);
    }
  };

  // Generate recertification narrative
  const handleGenerateNarrative = async () => {
    if (!patientId) return;
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/ehr/idg-summary/recertification/${patientId}/generate-narrative`, {}, { headers: getAuthHeaders() });
      setNarrative(response.data.narrative);
      toast.success("AI Narrative generated with Grade A quality");
    } catch (err) {
      // Demo narrative
      setNarrative(`
HOSPICE RECERTIFICATION NARRATIVE

Patient: ${summaryData?.patient_name || "John Smith"}
Date: ${new Date().toLocaleDateString()}
Certifying Physician: [MD Name]

CLINICAL JUSTIFICATION FOR CONTINUED HOSPICE ELIGIBILITY:

1. TERMINAL DIAGNOSIS: Cancer - Metastatic

2. DECLINE IN CLINICAL STATUS:
   - Pain Level: Moderate (2/3)
   - Dyspnea Level: Moderate with activity (2/3)
   - Functional Status: Declining - Assistance of 1 required

3. PROGNOSIS:
   Based on the above clinical findings and the natural progression of the disease, 
   it is my clinical judgment that if this disease runs its normal course, the patient's 
   life expectancy is six months or less.

4. SUPPORTING DOCUMENTATION:
   - HOPE Assessment: Complete
   - Recent Visit Notes: Available for review
   - IDG Discussion: Documented in meeting minutes

This narrative meets Grade A documentation standards.

Physician Signature: _________________________ Date: _____________
      `);
      toast.success("AI Narrative generated");
    } finally {
      setSubmitting(false);
    }
  };

  // Mark patient reviewed
  const handleMarkReviewed = async () => {
    if (!meetingId || !patientId) return;
    
    try {
      await axios.post(`${API_URL}/api/ehr/idg-summary/patient/${patientId}/mark-reviewed`, {
        patient_id: patientId,
        meeting_id: meetingId,
        hope_discussed: true,
        notes: "Patient reviewed at IDG meeting"
      }, { headers: getAuthHeaders() });
      
      toast.success("Patient marked as reviewed - QAPI Compliant");
      fetchSummary(patientId);
    } catch (err) {
      toast.error("Failed to mark patient as reviewed");
    }
  };

  // Complete meeting
  const handleCompleteMeeting = async () => {
    if (!meetingId) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/ehr/idg-summary/meeting/${meetingId}/complete`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.qapi_compliant) {
        toast.success("IDG Meeting completed - QAPI Compliant ✓");
      } else {
        toast.warning("IDG Meeting completed - QAPI Flag: HOPE data not discussed");
      }
    } catch (err) {
      toast.error("Failed to complete meeting");
    }
  };

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Use demo patient on load
  useEffect(() => {
    setPatientId("demo-patient");
    fetchSummary("demo-patient");
  }, []);

  if (loading && !summaryData) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="idg-summary-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-muted-foreground">Loading IDG Summary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="idg-summary-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="container mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ClipboardCheck className="w-5 h-5 text-slate-400" />
                <span className="text-slate-400 text-sm">IDG Meeting Hub</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-serif font-medium">
                Patient Huddle Sheet
              </h1>
              <p className="text-slate-300 mt-1">
                Single Source of Truth • Management by Exception
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                <Input
                  placeholder="Patient ID..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  className="bg-transparent border-0 text-white placeholder:text-slate-400 w-40"
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <Button size="sm" variant="secondary" onClick={handleSearch}>
                  Load
                </Button>
              </div>
              
              <Badge className="bg-green-500 text-white">
                {summaryData?.pre_populated_percentage || 90}% Pre-Populated
              </Badge>
            </div>
          </div>
          
          {/* Patient Header Card */}
          {summaryData && (
            <Card className="mt-4 bg-white/10 border-white/20">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-white">{summaryData.patient_name}</h2>
                      <p className="text-slate-300 text-sm">ID: {summaryData.patient_id}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{summaryData.benefit_period}</p>
                      <p className="text-xs text-slate-400">Benefit Period</p>
                    </div>
                    <Separator orientation="vertical" className="h-12 bg-white/20" />
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">Day {summaryData.days_in_program}</p>
                      <p className="text-xs text-slate-400">In Program</p>
                    </div>
                    <Separator orientation="vertical" className="h-12 bg-white/20" />
                    <div className="text-center">
                      <Badge className="text-lg px-3 py-1 bg-blue-500">{summaryData.current_loc}</Badge>
                      <p className="text-xs text-slate-400 mt-1">Level of Care</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="secondary" 
                      size="sm"
                      onClick={handleMarkReviewed}
                      data-testid="mark-reviewed-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Mark Reviewed
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={handleCompleteMeeting}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="complete-meeting-btn"
                    >
                      Complete Meeting
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Anomalies Banner */}
      {summaryData?.requires_discussion?.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="container mx-auto max-w-7xl px-6 py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-800">Requires Discussion:</span>
              <div className="flex flex-wrap gap-2">
                {summaryData.requires_discussion.map((item, idx) => (
                  <Badge key={idx} variant="outline" className="border-amber-500 text-amber-700 bg-amber-100">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl px-6 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Main Sections */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* 1. Compliance Health Bar */}
            <Card>
              <CardHeader 
                className="cursor-pointer flex flex-row items-center justify-between"
                onClick={() => toggleSection("compliance")}
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <CardTitle>1. Compliance Health Bar</CardTitle>
                </div>
                {expandedSections.compliance ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
              {expandedSections.compliance && summaryData?.compliance_health_bar && (
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* HOPE Status */}
                    <div className={`p-4 rounded-lg border-l-4 ${STATUS_COLORS[summaryData.compliance_health_bar.hope_status.status]?.bg} ${STATUS_COLORS[summaryData.compliance_health_bar.hope_status.status]?.border}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">HOPE Status</span>
                        <Badge className={STATUS_COLORS[summaryData.compliance_health_bar.hope_status.status]?.badge}>
                          {summaryData.compliance_health_bar.hope_status.status === "green" ? "Complete" : "Update Due"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{summaryData.compliance_health_bar.hope_status.message}</p>
                    </div>
                    
                    {/* F2F Status */}
                    <div className={`p-4 rounded-lg border-l-4 ${summaryData.compliance_health_bar.f2f_status.valid ? "bg-green-50 border-green-500" : "bg-red-50 border-red-500"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">F2F Status</span>
                        <Badge className={summaryData.compliance_health_bar.f2f_status.valid ? "bg-green-500" : "bg-red-500"}>
                          {summaryData.compliance_health_bar.f2f_status.valid ? "Valid" : "Needed"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{summaryData.compliance_health_bar.f2f_status.message}</p>
                    </div>
                    
                    {/* Documentation Grade */}
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Documentation Grade</span>
                        <Badge variant="outline" className="text-lg font-bold">
                          Grade {summaryData.compliance_health_bar.documentation_grade.grade}
                        </Badge>
                      </div>
                      {summaryData.compliance_health_bar.documentation_grade.ai_alert && (
                        <div className="flex items-start gap-2 mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded">
                          <Brain className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>AI Alert: {summaryData.compliance_health_bar.documentation_grade.ai_alert}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* SFV Trigger */}
                    <div className={`p-4 rounded-lg border ${summaryData.compliance_health_bar.sfv_trigger.active ? "bg-blue-50 border-blue-200" : "bg-slate-50"}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">SFV Trigger</span>
                        <Badge variant={summaryData.compliance_health_bar.sfv_trigger.active ? "default" : "secondary"}>
                          {summaryData.compliance_health_bar.sfv_trigger.active ? "Active" : "None"}
                        </Badge>
                      </div>
                      {summaryData.compliance_health_bar.sfv_trigger.note && (
                        <p className="text-sm text-green-700 mt-1">✓ {summaryData.compliance_health_bar.sfv_trigger.note}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* 2. Clinical Change in Status */}
            <Card>
              <CardHeader 
                className="cursor-pointer flex flex-row items-center justify-between"
                onClick={() => toggleSection("clinical")}
              >
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-red-600" />
                  <CardTitle>2. Clinical "Change in Status"</CardTitle>
                  {summaryData?.clinical_changes?.symptom_impact?.length > 0 && (
                    <Badge variant="destructive" className="ml-2">Changes Detected</Badge>
                  )}
                </div>
                {expandedSections.clinical ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
              {expandedSections.clinical && summaryData?.clinical_changes && (
                <CardContent className="space-y-4">
                  {/* Symptom Impact */}
                  {summaryData.clinical_changes.symptom_impact?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Stethoscope className="w-4 h-4" />
                        Symptom Impact (HOPE Section O/S)
                      </h4>
                      <div className="space-y-2">
                        {summaryData.clinical_changes.symptom_impact.map((change, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${change.alert_level === "high" ? "bg-red-50 border border-red-200" : "bg-slate-50"}`}>
                            <div className="flex items-center gap-2">
                              {change.direction === "increased" ? (
                                <TrendingUp className="w-4 h-4 text-red-500" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-green-500" />
                              )}
                              <span className="font-medium">{change.symptom}:</span>
                              <span>
                                Moved from [{change.previous_score}-{change.previous_label}] to [{change.current_score}-{change.current_label}]
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Functional Status */}
                  {summaryData.clinical_changes.functional_status && (
                    <div>
                      <h4 className="font-medium mb-2">Functional Status (Section GG)</h4>
                      <div className="p-3 rounded-lg bg-slate-50">
                        <p><strong>Mobility:</strong> {summaryData.clinical_changes.functional_status.mobility}</p>
                        <p><strong>Transfer Status:</strong> {summaryData.clinical_changes.functional_status.transfer_status}</p>
                        {summaryData.clinical_changes.functional_status.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{summaryData.clinical_changes.functional_status.notes}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Weight Trend */}
                  {summaryData.clinical_changes.weight_trend && (
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Scale className="w-4 h-4" />
                        Weight Trend
                      </h4>
                      <div className={`p-3 rounded-lg ${summaryData.clinical_changes.weight_trend.alert ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
                        <div className="flex items-center gap-4">
                          <span className="text-2xl font-bold">{summaryData.clinical_changes.weight_trend.current_weight} lbs</span>
                          <Badge variant={summaryData.clinical_changes.weight_trend.weight_change < 0 ? "destructive" : "default"}>
                            {summaryData.clinical_changes.weight_trend.direction} of {Math.abs(summaryData.clinical_changes.weight_trend.weight_change)} lbs
                          </Badge>
                          <span className="text-sm text-muted-foreground">in last {summaryData.clinical_changes.weight_trend.period_days} days</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Recent Incidents */}
                  {summaryData.clinical_changes.recent_incidents?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Recent Incidents
                      </h4>
                      {summaryData.clinical_changes.recent_incidents.map((incident, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{incident.type}</span>
                            <span className="text-sm text-muted-foreground">{incident.date}</span>
                          </div>
                          <p className="text-sm mt-1">{incident.description}</p>
                          {incident.equipment_ordered && (
                            <p className="text-sm text-green-700 mt-1">✓ Safety equipment ordered: {incident.equipment_ordered}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* 3. Medication & Order Insights */}
            <Card>
              <CardHeader 
                className="cursor-pointer flex flex-row items-center justify-between"
                onClick={() => toggleSection("medications")}
              >
                <div className="flex items-center gap-2">
                  <Pill className="w-5 h-5 text-purple-600" />
                  <CardTitle>3. Medication & Order Insights</CardTitle>
                  {summaryData?.medication_insights?.high_risk_meds?.length > 0 && (
                    <Badge variant="outline" className="border-purple-500 text-purple-700 ml-2">
                      {summaryData.medication_insights.high_risk_meds.length} High-Risk
                    </Badge>
                  )}
                </div>
                {expandedSections.medications ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
              {expandedSections.medications && summaryData?.medication_insights && (
                <CardContent className="space-y-4">
                  {/* High-Risk Meds */}
                  {summaryData.medication_insights.high_risk_meds?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-red-700">High-Risk Medications</h4>
                      <div className="space-y-2">
                        {summaryData.medication_insights.high_risk_meds.map((med, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center justify-between">
                            <div>
                              <p className="font-medium">{med.name}</p>
                              <p className="text-sm text-muted-foreground">{med.dose} • {med.reason}</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedMed(med);
                                setShowMedUpdateDialog(true);
                              }}
                            >
                              <Pen className="w-3 h-3 mr-1" />
                              Update
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Unrelated Meds */}
                  {summaryData.medication_insights.unrelated_meds?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2 text-amber-700">Unrelated Medications (Addendum)</h4>
                      <div className="space-y-2">
                        {summaryData.medication_insights.unrelated_meds.map((med, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                            <p className="font-medium">{med.name}</p>
                            <p className="text-sm text-muted-foreground">{med.dose} • {med.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* New Orders */}
                  {summaryData.medication_insights.new_orders_since_last_idg?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">New Orders Since Last IDG</h4>
                      <div className="space-y-2">
                        {summaryData.medication_insights.new_orders_since_last_idg.map((order, idx) => (
                          <div key={idx} className="p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{order.type}</Badge>
                              <span>{order.item}</span>
                            </div>
                            <Badge variant={order.status === "Active" || order.status === "Delivered" ? "default" : "secondary"}>
                              {order.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* 4. Psychosocial & Spiritual */}
            <Card>
              <CardHeader 
                className="cursor-pointer flex flex-row items-center justify-between"
                onClick={() => toggleSection("psychosocial")}
              >
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-600" />
                  <CardTitle>4. Psychosocial & Spiritual (HOPE Section L)</CardTitle>
                </div>
                {expandedSections.psychosocial ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </CardHeader>
              {expandedSections.psychosocial && summaryData?.psychosocial_spiritual && (
                <CardContent className="space-y-4">
                  {/* Patient Goal */}
                  <div className="p-4 rounded-lg bg-gradient-to-r from-pink-50 to-purple-50 border">
                    <h4 className="font-medium mb-1">Patient Goal</h4>
                    <p className="text-lg italic">"{summaryData.psychosocial_spiritual.patient_goal}"</p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Caregiver Burden */}
                    <div className={`p-4 rounded-lg border ${
                      summaryData.psychosocial_spiritual.caregiver_burden.level === "High" ? "bg-red-50 border-red-200" :
                      summaryData.psychosocial_spiritual.caregiver_burden.level === "Moderate" ? "bg-amber-50 border-amber-200" :
                      "bg-green-50 border-green-200"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Caregiver Burden</h4>
                        <Badge variant={
                          summaryData.psychosocial_spiritual.caregiver_burden.level === "High" ? "destructive" :
                          summaryData.psychosocial_spiritual.caregiver_burden.level === "Moderate" ? "default" : "secondary"
                        }>
                          {summaryData.psychosocial_spiritual.caregiver_burden.level}
                        </Badge>
                      </div>
                      {summaryData.psychosocial_spiritual.caregiver_burden.notes && (
                        <p className="text-sm">Social Work Note: {summaryData.psychosocial_spiritual.caregiver_burden.notes}</p>
                      )}
                      {summaryData.psychosocial_spiritual.caregiver_burden.respite_discussed && (
                        <p className="text-sm text-green-700 mt-1">✓ Respite discussed</p>
                      )}
                    </div>
                    
                    {/* Spiritual Support */}
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                      <h4 className="font-medium mb-2">Spiritual Support</h4>
                      <p><strong>Preference:</strong> {summaryData.psychosocial_spiritual.spiritual_support.preference}</p>
                      <p><strong>Chaplain visits:</strong> {summaryData.psychosocial_spiritual.spiritual_support.chaplain_visits_last_period} in last period</p>
                      <p>
                        <strong>Accepting visits:</strong> {summaryData.psychosocial_spiritual.spiritual_support.accepting_visits ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>

          {/* Right Column - Action Panel */}
          <div className="space-y-4">
            
            {/* 5. IDG Collaborative Plan */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  <CardTitle>5. IDG Collaborative Plan</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* LOC Recommendation */}
                <div className="p-4 rounded-lg bg-slate-50 border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Level of Care</h4>
                    <Button size="sm" variant="outline" onClick={() => setShowLOCDialog(true)}>
                      Change
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-lg px-3">{summaryData?.collaborative_plan?.loc_recommendation?.current}</Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline" className="text-lg px-3">
                      {summaryData?.collaborative_plan?.loc_recommendation?.recommended}
                    </Badge>
                  </div>
                </div>
                
                {/* Visit Frequency */}
                <div className="p-4 rounded-lg bg-slate-50 border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Visit Frequency</h4>
                    <Button size="sm" variant="outline" onClick={() => setShowFrequencyDialog(true)}>
                      Adjust
                    </Button>
                  </div>
                  {summaryData?.collaborative_plan?.visit_frequency?.current && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>RN: {summaryData.collaborative_plan.visit_frequency.current.rn}x/week</div>
                      <div>HHA: {summaryData.collaborative_plan.visit_frequency.current.hha}x/week</div>
                      <div>MSW: {summaryData.collaborative_plan.visit_frequency.current.msw}x/month</div>
                      <div>Chaplain: {summaryData.collaborative_plan.visit_frequency.current.chaplain}x/month</div>
                    </div>
                  )}
                </div>
                
                {/* Team Signatures */}
                <div className="p-4 rounded-lg bg-slate-50 border">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Team Consensus</h4>
                    <Button size="sm" onClick={() => setShowSignDialog(true)}>
                      <FileSignature className="w-4 h-4 mr-1" />
                      Sign All
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {signatures.length > 0 ? `${signatures.length} signatures collected` : "No signatures yet"}
                  </p>
                </div>
                
                {/* Recertification */}
                {summaryData?.collaborative_plan?.recertification_due && (
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <h4 className="font-medium text-amber-800">Recertification Due</h4>
                    </div>
                    <Button 
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={() => {
                        handleGenerateNarrative();
                        setShowNarrativeDialog(true);
                      }}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate MD Narrative
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 6. Financial Oversight */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  <CardTitle>6. Financial Oversight</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {summaryData?.financial_oversight && (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${summaryData.financial_oversight.exceeds_threshold ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Per Patient Day (PPD)</span>
                        <Badge variant={summaryData.financial_oversight.exceeds_threshold ? "destructive" : "default"}>
                          {summaryData.financial_oversight.percentile}
                        </Badge>
                      </div>
                      <p className="text-3xl font-bold">${summaryData.financial_oversight.per_patient_day}</p>
                      <p className="text-sm text-muted-foreground">Threshold: ${summaryData.financial_oversight.threshold}/day</p>
                    </div>
                    
                    {/* Cost Breakdown */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Medications</span>
                        <span>${summaryData.financial_oversight.cost_breakdown?.medications || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Supplies</span>
                        <span>${summaryData.financial_oversight.cost_breakdown?.supplies || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>DME</span>
                        <span>${summaryData.financial_oversight.cost_breakdown?.dme || 0}</span>
                      </div>
                    </div>
                    
                    {summaryData.financial_oversight.alert && (
                      <div className="p-3 rounded-lg bg-red-100 border border-red-300 text-sm text-red-800">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        {summaryData.financial_oversight.alert}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* QAPI Compliance */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  <CardTitle>QAPI Compliance</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {summaryData?.qapi_compliance && (
                  <div className={`p-4 rounded-lg ${summaryData.qapi_compliance.compliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {summaryData.qapi_compliance.compliant ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className="font-medium">
                        {summaryData.qapi_compliance.compliant ? "Compliant" : "Review Required"}
                      </span>
                    </div>
                    <p className="text-sm">
                      Last IDG Review: {summaryData.qapi_compliance.last_idg_review || "Not recorded"}
                    </p>
                    <p className="text-sm">
                      Days since review: {summaryData.qapi_compliance.days_since_review}
                    </p>
                    {!summaryData.qapi_compliance.compliant && (
                      <p className="text-sm text-red-700 mt-2">
                        ⚠️ HOPE updates must be discussed every 15 days
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Medication Update Dialog */}
      <Dialog open={showMedUpdateDialog} onOpenChange={setShowMedUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Live Medication Update</DialogTitle>
            <DialogDescription>
              Changes will simultaneously update MAR, Orders, and Pharmacy feed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{selectedMed?.name}</p>
              <p className="text-sm text-muted-foreground">Current: {selectedMed?.dose}</p>
            </div>
            <div>
              <Label>New Dose</Label>
              <Input 
                placeholder="e.g., 20mg every 4 hours"
                value={newDose}
                onChange={(e) => setNewDose(e.target.value)}
              />
            </div>
            <div>
              <Label>Reason for Change</Label>
              <Textarea 
                placeholder="Clinical rationale..."
                value={doseReason}
                onChange={(e) => setDoseReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMedUpdateDialog(false)}>Cancel</Button>
            <Button onClick={handleMedUpdate} disabled={submitting || !newDose}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Update All Systems
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LOC Dialog */}
      <Dialog open={showLOCDialog} onOpenChange={setShowLOCDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Level of Care</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Level of Care</Label>
              <Select value={newLOC} onValueChange={setNewLOC}>
                <SelectTrigger>
                  <SelectValue placeholder="Select LOC..." />
                </SelectTrigger>
                <SelectContent>
                  {LOC_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <p className="font-medium">{opt.value} - {opt.label}</p>
                        <p className="text-xs text-muted-foreground">{opt.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rationale</Label>
              <Textarea 
                placeholder="Clinical justification..."
                value={locRationale}
                onChange={(e) => setLocRationale(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLOCDialog(false)}>Cancel</Button>
            <Button onClick={handleLOCUpdate} disabled={submitting || !newLOC}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Update LOC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visit Frequency Dialog */}
      <Dialog open={showFrequencyDialog} onOpenChange={setShowFrequencyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Visit Frequency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>RN Visits/Week</Label>
                <Input 
                  type="number" 
                  min="0" 
                  max="7"
                  value={visitFrequency.rn}
                  onChange={(e) => setVisitFrequency({...visitFrequency, rn: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>HHA Visits/Week</Label>
                <Input 
                  type="number" 
                  min="0" 
                  max="7"
                  value={visitFrequency.hha}
                  onChange={(e) => setVisitFrequency({...visitFrequency, hha: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>MSW Visits/Month</Label>
                <Input 
                  type="number" 
                  min="0" 
                  max="4"
                  value={visitFrequency.msw}
                  onChange={(e) => setVisitFrequency({...visitFrequency, msw: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Chaplain Visits/Month</Label>
                <Input 
                  type="number" 
                  min="0" 
                  max="4"
                  value={visitFrequency.chaplain}
                  onChange={(e) => setVisitFrequency({...visitFrequency, chaplain: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea 
                placeholder="Reason for adjustment..."
                value={frequencyNotes}
                onChange={(e) => setFrequencyNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFrequencyDialog(false)}>Cancel</Button>
            <Button onClick={handleFrequencyUpdate} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Update Frequency
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign All Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Team Consensus Signature</DialogTitle>
            <DialogDescription>Electronic "Sign All" for present members</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {["RN", "MD", "MSW", "Chaplain", "HHA", "Coordinator"].map(role => (
                <Button 
                  key={role} 
                  variant={signatures.includes(role) ? "default" : "outline"}
                  className="justify-start"
                  onClick={() => {
                    if (signatures.includes(role)) {
                      setSignatures(signatures.filter(s => s !== role));
                    } else {
                      setSignatures([...signatures, role]);
                    }
                  }}
                >
                  {signatures.includes(role) ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2 opacity-30" />}
                  {role}
                </Button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {signatures.length} of 6 roles signed
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)}>Cancel</Button>
            <Button onClick={() => { setShowSignDialog(false); toast.success("Team signatures recorded"); }}>
              <FileSignature className="w-4 h-4 mr-2" />
              Confirm Signatures
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Narrative Dialog */}
      <Dialog open={showNarrativeDialog} onOpenChange={setShowNarrativeDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              AI-Assisted Recertification Narrative
            </DialogTitle>
            <DialogDescription>Grade A documentation for MD signature</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {submitting ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                <span className="ml-2">Generating narrative...</span>
              </div>
            ) : (
              <Textarea 
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                rows={20}
                className="font-mono text-sm"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNarrativeDialog(false)}>Close</Button>
            <Button onClick={() => {
              navigator.clipboard.writeText(narrative);
              toast.success("Narrative copied to clipboard");
            }}>
              Copy to Clipboard
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700">
              <Send className="w-4 h-4 mr-2" />
              Send to MD
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
